import React, { createContext, useContext } from 'react';
import { ImpressionItem } from './ImpressionItem';
import { ViewMode } from '../Attachments/InlineAttachment';
import { useActiveUserId } from '../../../../../hooks';
import { useSendAgentFeedback } from '../../../../../services/AgentsService';

export type ImpressionEditorContextType = {
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  activeImpression: ImpressionItem;
  viewMode: ViewMode;
  patient: fhir.Patient & { id: string };
  refresh: () => void;
  sendAgentFeedback: ReturnType<typeof useSendAgentFeedback>;
  activeUserId: string;
};

const ImpressionEditorContext = createContext<
  ImpressionEditorContextType | undefined
>(undefined);

export const ImpressionEditorProvider: React.FC<{
  patient: fhir.Patient & { id: string };
  children: React.ReactNode;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  activeImpression: ImpressionItem;
  viewMode: ViewMode;
  refresh: () => void;
}> = ({
  children,
  patient,
  isEditing,
  setIsEditing,
  activeImpression,
  viewMode,
  refresh,
}) => {
  const activeUserId = useActiveUserId();
  const sendAgentFeedback = useSendAgentFeedback();
  return (
    <ImpressionEditorContext.Provider
      value={{
        isEditing,
        activeImpression,
        setIsEditing,
        viewMode,
        patient,
        refresh,
        sendAgentFeedback,
        activeUserId,
      }}
    >
      {children}
    </ImpressionEditorContext.Provider>
  );
};

// Custom hook to use the ImpressionEditorContext
export const useImpressionEditorContext = (): ImpressionEditorContextType => {
  const context = useContext(ImpressionEditorContext);
  if (!context) {
    throw new Error(
      'useImpressionEditorContext must be used within an ImpressionEditorProvider',
    );
  }
  return context;
};
